import React, { useEffect, useRef } from "react";
import { BIGFLAKES, FLAKES, FLAKES_SOFT } from "./atlas";

/**
 * Falling snow, drawn on one canvas from the pixel snowflake sheets.
 *
 * A canvas rather than DOM sprites: a hundred flakes as elements would be a
 * hundred compositor layers, here it is one draw call each per frame. Flakes
 * land on whole device pixels at whole-number scales with smoothing off, so the
 * pixel art stays sharp on any screen.
 *
 * `soft` uses the outline-free flakes, which look best over the night pages;
 * the default outlined ones stay visible over white content too. `density`
 * scales how many there are (1 = the site's normal fall).
 */

/** Flakes per square pixel at density 1, and the bounds on the count. */
var PER_PIXEL = 1 / 9500;
var MIN_FLAKES = 30;
var MAX_FLAKES = 220;

export default function Snowfall({ soft, density, className }) {
  const ref = useRef(null);

  useEffect(
    function () {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return undefined;
      }
      var canvas = ref.current;
      var ctx = canvas.getContext("2d");
      var sheet = soft ? FLAKES_SOFT : FLAKES;
      var small = new Image();
      small.src = sheet.src;
      var big = new Image();
      big.src = BIGFLAKES.src;
      var bigNames = Object.keys(BIGFLAKES.rects);
      var amount = density === undefined ? 1 : density;

      var w = 0, h = 0, dpr = 1;
      var flakes = [];
      var wind = 0, windTarget = 0, windAt = 0;
      var raf = 0, last = 0;

      function flake(anywhere) {
        // Depth drives everything: near flakes are faster, brighter, and the
        // nearest few are drawn at double size.
        var z = 0.35 + Math.random() * 0.65;
        var isBig = Math.random() < 0.015;
        var r = Math.random();
        // The sheet runs from the largest flakes (0-3) down to specks (12-15):
        // mostly small and medium, now and then a large one.
        var index = r < 0.12 ? Math.floor(Math.random() * 4) : 4 + Math.floor(Math.random() * 12);
        return {
          x: Math.random() * w,
          y: anywhere ? Math.random() * h : -40 - Math.random() * 60,
          z: z,
          speed: isBig ? 18 + z * 14 : 22 + z * 58,
          amp: 8 + Math.random() * 22,
          freq: 0.6 + Math.random() * 1.1,
          phase: Math.random() * Math.PI * 2,
          scale: !isBig && z > 0.96 ? 2 : 1,
          alpha: isBig ? 0.8 : 0.3 + z * 0.6,
          rect: isBig ? BIGFLAKES.rects[bigNames[Math.floor(Math.random() * bigNames.length)]] : sheet.rects[index],
          big: isBig,
        };
      }

      function resize() {
        dpr = window.devicePixelRatio || 1;
        w = canvas.clientWidth;
        h = canvas.clientHeight;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.imageSmoothingEnabled = false;
        var target = Math.max(MIN_FLAKES, Math.min(MAX_FLAKES, Math.round(w * h * PER_PIXEL * amount)));
        while (flakes.length < target) flakes.push(flake(true));
        flakes.length = target;
      }

      function frame(now) {
        var dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
        last = now;
        var t = now / 1000;
        // A breeze that changes its mind every few seconds.
        if (t > windAt) {
          windTarget = (Math.random() - 0.5) * 36;
          windAt = t + 4 + Math.random() * 6;
        }
        wind += (windTarget - wind) * Math.min(1, dt * 0.6);

        ctx.clearRect(0, 0, w, h);
        for (var i = 0; i < flakes.length; i++) {
          var f = flakes[i];
          f.y += f.speed * dt;
          f.x += (Math.cos(t * f.freq + f.phase) * f.amp * 0.6 + wind * f.z) * dt;
          var rw = f.rect[2] * f.scale, rh = f.rect[3] * f.scale;
          if (f.y > h + rh || f.x < -rw - 40 || f.x > w + 40) {
            flakes[i] = f = flake(false);
            continue;
          }
          var img = f.big ? big : small;
          if (!img.complete) continue;
          ctx.globalAlpha = f.alpha;
          ctx.drawImage(
            img,
            f.rect[0], f.rect[1], f.rect[2], f.rect[3],
            Math.round(f.x * dpr) / dpr, Math.round(f.y * dpr) / dpr, rw, rh
          );
        }
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(frame);
      }

      resize();
      window.addEventListener("resize", resize);
      raf = requestAnimationFrame(frame);
      return function () {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", resize);
      };
    },
    [soft, density]
  );

  return <canvas ref={ref} className={className || "xm-snow"} aria-hidden="true" />;
}
